import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';

export enum EmailTemplateType {
  VENDOR_MAGIC_LINK = 'VENDOR_MAGIC_LINK',
  PROMOTER_MAGIC_LINK = 'PROMOTER_MAGIC_LINK',
  PROSPECT_MAGIC_LINK = 'PROSPECT_MAGIC_LINK',
  PROSPECT_WELCOME = 'PROSPECT_WELCOME',
  PASSWORDLESS_LOGIN = 'PASSWORDLESS_LOGIN',
  TESTIMONIAL_APPROVED = 'TESTIMONIAL_APPROVED',
  TESTIMONIAL_REJECTED = 'TESTIMONIAL_REJECTED',
  PROSPECT_FINALIZED = 'PROSPECT_FINALIZED',
  TEAM_MEMBER_INVITE = 'TEAM_MEMBER_INVITE',
  INVOICE = 'INVOICE',
  // DEV-187 — reward notifications split per reward method.
  REWARD_GIFT_CARD = 'REWARD_GIFT_CARD',
  REWARD_DISCOUNT = 'REWARD_DISCOUNT',
  REWARD_CHOICE = 'REWARD_CHOICE',
  REFERRAL_INVITATION = 'REFERRAL_INVITATION',
  SURVEY_INVITATION = 'SURVEY_INVITATION',
}

export enum EmailFormat {
  HTML = 'HTML',
  TEXT = 'TEXT',
}

export interface TemplateVariable {
  name: string;
  description: string;
  example: string;
}

export interface EmailTemplateResponse {
  id: string;
  type: EmailTemplateType;
  subject: string;
  body: string;
  format: EmailFormat;
  isActive: boolean;
  availableVariables: TemplateVariable[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailTemplateRequest {
  type: EmailTemplateType;
  subject: string;
  body: string;
  format: EmailFormat;
}

export interface UpdateEmailTemplateRequest {
  subject?: string;
  body?: string;
  format?: EmailFormat;
  isActive?: boolean;
}

export interface PreviewEmailTemplateRequest {
  type: EmailTemplateType;
  subject: string;
  body: string;
  format: EmailFormat;
  sampleData?: Record<string, string>;
}

export interface PreviewEmailTemplateResponse {
  subject: string;
  body: string;
}

export interface RenderEmailPreviewRequest {
  templateType: EmailTemplateType;
  firstName: string;
  email: string;
  productNames?: string[];
}

export const emailTemplateApi = createApi({
  reducerPath: 'emailTemplateApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['EmailTemplate'],
  endpoints: (builder) => ({
    // Get all email templates
    getEmailTemplates: builder.query<EmailTemplateResponse[], void>({
      query: () => '/vendor/email-templates',
      providesTags: ['EmailTemplate'],
    }),

    // Get specific template by type
    getEmailTemplate: builder.query<EmailTemplateResponse, EmailTemplateType>({
      query: (type) => `/vendor/email-templates/${type}`,
      providesTags: (_result, _error, type) => [{ type: 'EmailTemplate', id: type }],
    }),

    // Create or update template
    createOrUpdateEmailTemplate: builder.mutation<EmailTemplateResponse, CreateEmailTemplateRequest>({
      query: (body) => ({
        url: '/vendor/email-templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmailTemplate'],
    }),

    // Update existing template
    updateEmailTemplate: builder.mutation<EmailTemplateResponse, { type: EmailTemplateType; data: UpdateEmailTemplateRequest }>({
      query: ({ type, data }) => ({
        url: `/vendor/email-templates/${type}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { type }) => [{ type: 'EmailTemplate', id: type }, 'EmailTemplate'],
    }),

    // Reset template to default
    resetEmailTemplate: builder.mutation<EmailTemplateResponse, EmailTemplateType>({
      query: (type) => ({
        url: `/vendor/email-templates/${type}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, type) => [{ type: 'EmailTemplate', id: type }, 'EmailTemplate'],
    }),

    // Preview template
    previewEmailTemplate: builder.mutation<PreviewEmailTemplateResponse, PreviewEmailTemplateRequest>({
      query: (body) => ({
        url: '/vendor/email-templates/preview',
        method: 'POST',
        body,
      }),
    }),

    // Render email preview with real vendor data (logo, products, rewards)
    renderEmailPreview: builder.mutation<PreviewEmailTemplateResponse, RenderEmailPreviewRequest>({
      query: (body) => ({
        url: '/vendor/email-templates/render-preview',
        method: 'POST',
        body,
      }),
    }),

    // Get available variables for template type
    getAvailableVariables: builder.query<TemplateVariable[], EmailTemplateType>({
      query: (type) => `/vendor/email-templates/${type}/variables`,
    }),
  }),
});

export const {
  useGetEmailTemplatesQuery,
  useGetEmailTemplateQuery,
  useCreateOrUpdateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useResetEmailTemplateMutation,
  usePreviewEmailTemplateMutation,
  useGetAvailableVariablesQuery,
  useRenderEmailPreviewMutation,
  useLazyGetEmailTemplateQuery,
  useLazyGetAvailableVariablesQuery,
} = emailTemplateApi;
